import { first, omit } from 'lodash';

import CONF from 'config/conf.singleton';
import H from 'constants/httpMethod.const';
import PAYMENT_TYPE from 'constants/paymentType.const';
import { AUTH_API } from 'middleware/api';
import asArray from 'utils/arrays/asArray.util';
import authenticatedEndpoint from 'utils/backend/authenticatedEndpoint.util';
import jsonEndpoint from 'utils/backend/jsonEndpoint.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import logger from 'utils/logger.util';
import parseMoney from 'utils/parsers/parseMoney.util';
import isNotEnumVal from 'utils/predicates/enum/isNotEnumVal.util';

import { TRANSACTION_CREATE_FAILURE, TRANSACTION_CREATE_REQUEST, TRANSACTION_CREATE_SUCCESS } from 'checkout/constants';

const L = logger('beCreateTransaction');

const DEFAULT_INTENT_ID = void 1;
const ALLOWED = Object.freeze([PAYMENT_TYPE.slickPos, PAYMENT_TYPE.onlineCard]);

const omitId = (object) => omit(object, ['id']);
const omitExtraOptions = (object) => omit(object, ['salonId', 'appointmentDetails', 'paymentIntentId', 'paymentType']);

const updatedVouchers = (vouchers) => {
  const filtered = asArray(vouchers).filter((v) => v && v.used_amount);
  for (const voucher of filtered) {
    voucher.amount_change = -voucher.used_amount;
  }
  return filtered;
};

// NOTE: BE will complain with 400 if intent_id is not set for online_card and slick_pos,
// NOTE: BE will also complain with 400 if it is set for other cases
// SEE: https://getslick.atlassian.net/browse/GS-10809
// SEE: https://getslick.atlassian.net/browse/GS-10811

export const normalizeIntentId = (options) => {
  const paymentType = options?.paymentType;

  if (isNotEnumVal(PAYMENT_TYPE, paymentType)) L.warn('normalizeIntentId()', 'Invalid paymentType:', paymentType);

  if (!ALLOWED.includes(paymentType)) return DEFAULT_INTENT_ID;

  const paymentIntentId = fromAnyToTrimmed(options?.paymentIntentId);
  if (!paymentIntentId) L.warn('normalizeIntentId()', 'Invalid paymentIntentId:', options?.paymentIntentId);

  return paymentIntentId || DEFAULT_INTENT_ID;
};

const beCreateTransaction = (options) => {
  const {
    salonId,
    effective_date: effectiveDate,
    otherAmount,
    cardAmount,
    clients: clientsFromOptions,
    partCash,
    created,
    onlineAmount,
    depositListData,
    appointmentDetails,
    paymentIntentId,
    paymentType,
  } = options ?? {};

  const data = omitExtraOptions(options);
  const clients = asArray(clientsFromOptions).map((c) => (c.id === 0 ? omitId(c) : c));
  const { appointmentId, id: clientId } = first(clients) ?? {};

  if (!effectiveDate && created) {
    data.effective_date = created;
  }

  // there is appointment not just retail, not walk in
  const details = appointmentDetails?.[appointmentId];
  if (appointmentId && clientId && appointmentDetails && details) {
    // TODO: probably the if should also need to change to offset the possible throw
    const appointmentStartTime = details?.response?.procedures?.[0]?.start_time;
    if (data.effective_date !== appointmentStartTime) data.effective_date = appointmentStartTime;
  }

  if (!data.effective_date) delete data.effective_date;

  const reducedDeposits = asArray(depositListData)
    .filter(({ selected }) => selected === true)
    .map((d) => Object.assign(d, { salonId, amount: d.use_deposit }));

  const parsedTotal = parseMoney({ value: cardAmount + onlineAmount + partCash + otherAmount });

  data.appointment_id = appointmentId;
  data.clients = clients;
  data.deposits = reducedDeposits;
  data.intent_id = normalizeIntentId({ paymentIntentId, paymentType });
  data.paymentType = paymentType;
  data.total = parsedTotal.coin.str;
  data.vouchers_used = updatedVouchers(data.vouchers_used);

  return {
    [AUTH_API]: {
      ...authenticatedEndpoint({ root: CONF.transactionApiRoot }),
      ...jsonEndpoint(H.post, `checkout/create/${salonId}/`, data),
      types: [TRANSACTION_CREATE_REQUEST, TRANSACTION_CREATE_SUCCESS, TRANSACTION_CREATE_FAILURE],
    },
  };
};

export default beCreateTransaction;
